import { DatePipe } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSortModule, Sort } from "@angular/material/sort";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { debounceTime, distinctUntilChanged, forkJoin, Observable, of, Subject, Subscription, switchMap, tap } from "rxjs";
import { take } from "rxjs/operators";
import { Chat, ChatGroup, ChatType } from "src/app/classes/chat";
import { User } from "src/app/classes/user";
import { AuthenticationService } from "src/app/services/authentication.service";
import { ChatService } from "src/app/services/chat.service";
import { UserProfileImageService } from "src/app/services/user-profile-image.service";


enum GroupChatEditorMode{ create, view, edit };


@Component( {
	selector: "group-chat-editor",
	templateUrl: "group-chat-editor.component.html",
	styleUrl: "group-chat-editor.component.scss",
	standalone: true,
	imports: [
		FormsModule, 
		MatButtonModule, MatCheckboxModule,
		MatIconModule, 
		MatProgressBarModule, MatProgressSpinnerModule, 
		RouterModule,
		DatePipe,
		MatSnackBarModule,
		MatSortModule
	]
} )
export class GroupChatEditorComponent implements OnInit, OnDestroy {


	bInProgress = false;
	bFirstLoading = true;
	bFirstLoadingError = false;
	subscriptions = new Subscription();

	myUserId = "";

	contacts: User[] = [];
	contactsFiltered: User[] = [];
	contactNames: { [key:string]: string } = {};
	contactVerein: { [key:string]: string } = {};
	contactBeingEdited: { [key:string]: boolean } = {};
	contactAvatarUrls: Record<string, string | null> = {};
	readonly defaultAvatar = "assets/user.png";
	groups: ChatGroup[] = [];

	contactIdsNewGroup: string[] = [];
	newTitle = "";
	bMissingTitle = false;
	bNotEnoughMembers = false;
	strContactFilter = "";
	bNotifications = false;

	chatId = "";
	chatGroupId = "";
	bSystem = false;
	chatGroup: ChatGroup|null = null;

	public GroupChatEditorMode = GroupChatEditorMode; // to use it in the template
	groupChatEditorMode = GroupChatEditorMode.create;

	bSavingError = false;
	tmsLastSave = 0;
	groupEditSubject = new Subject<string>();
	groupEditObs = this.groupEditSubject.asObservable();


	constructor(
		private chatService: ChatService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private authenticationService: AuthenticationService,
		private matSnackBar: MatSnackBar,
		private userProfileImageService: UserProfileImageService
	) {}


	ngOnInit() {

		this.subscriptions.add(
			this.activatedRoute.queryParams.subscribe( params => {
				const chatId = params['chat'];
				const chatGroupId = params['gruppe'];
				const bSystem = params['veranstaltung'];
				if( chatGroupId && chatId ) {
					this.chatGroupId = chatGroupId;
					this.chatId = chatId;
					this.bSystem = (bSystem == "true");
				}
			})
		);

		this.subscriptions.add(
			this.groupEditObs.pipe(
				debounceTime( 400 ),
				distinctUntilChanged(),
				switchMap( newTitle => {

					if( !newTitle ) {
						this.bMissingTitle = true;
						return of(null);
					}

					const chatGroup = new ChatGroup();
					chatGroup.id = this.chatGroupId;
					chatGroup.title = this.newTitle;

					this.bInProgress = true;
					this.bSavingError = false;
			
					return this.chatService.editChatGroup( chatGroup ).pipe(
						tap( result => {

							this.bInProgress = false;

							if( !result ) {
								this.bSavingError = true;
								return;
							}
				
							if( this.chatGroup ) this.chatGroup.title = result.title;
							this.bSavingError = false;
							this.autoSaveDone();

						})
					);
					
				} ),
			).subscribe()
		);

		this.myUserId = this.authenticationService.getCurrentUser().ebeId;

		const obss: {
			contacts: Observable<User[]|null>,
			chat: Observable<Chat|null>,
			chatGroup: Observable<ChatGroup|null>,
			systemUsers: Observable<string[]|null>
		} = {
			contacts: this.chatService.getContacts(),
			chat: of(null),
			chatGroup: of(null),
			systemUsers: of(null)
		};

		const bExists = this.chatId && this.chatGroupId;
		if( bExists ) {
			obss.chat = this.chatService.getChat( this.chatId );
			if( this.bSystem ) {
				obss.systemUsers = this.chatService.getSystemChatGroupUsers( this.chatGroupId );
			}
			else {
				obss.chatGroup = this.chatService.getChatGroup( this.chatGroupId );
			}
		}

		forkJoin( obss ).subscribe( answers => {

			this.bFirstLoading = false;

			if( !answers || !answers.contacts ) {
				this.bFirstLoadingError = true;
				return;
			}

			if( bExists ) {

				if( !answers.chat ) {
					this.bFirstLoadingError = true;
					return;
				}

				if( this.bSystem ) {

					if( !answers.systemUsers ) {
						this.bFirstLoadingError = true;
						return;
					}

					this.newTitle = answers.chat.title;
					this.contactIdsNewGroup = answers.systemUsers;
					this.groupChatEditorMode = GroupChatEditorMode.view;

				}
				else {

					if( !answers.chatGroup ) {
						this.bFirstLoadingError = true;
						return;
					}

					this.chatGroup = answers.chatGroup;
					this.newTitle = this.chatGroup.title;
					this.contactIdsNewGroup = this.chatGroup.users;
					this.groupChatEditorMode = (this.chatGroup.bAdmin || this.authenticationService.isAdmin())
						? GroupChatEditorMode.edit
						: GroupChatEditorMode.view;

				}

				this.bNotifications = answers.chat.bNotifications;

			}
			else {
				this.contactIdsNewGroup = [ this.myUserId ];
			}

			this.contacts = answers.contacts
				.sort( (a,b) => {
					const arrA: string[] = [];
					if( a.ebeVorname ) arrA.push( a.ebeVorname );
					if( a.ebeName ) arrA.push( a.ebeName );
					if( arrA.length ) arrA.push( a.ebeId );
					const nameA = arrA.join( " " );
					const arrB: string[] = [];
					if( b.ebeVorname ) arrB.push( b.ebeVorname );
					if( b.ebeName ) arrB.push( b.ebeName );
					if( arrB.length ) arrB.push( b.ebeId );
					const nameB = arrB.join( " " );

					return nameA.localeCompare( nameB );
				} );
			this.contactsFiltered = this.contacts;

		for( const contact of this.contacts ) {

			const arr = [];
			if( contact.ebeVorname ) arr.push( contact.ebeVorname );
			if( contact.ebeName ) arr.push( contact.ebeName );
			this.contactNames[ contact.ebeId ] = arr.join( " " ) || contact.ebeId;
			
			this.contactVerein[ contact.ebeId ] = contact.mitgliedsname || contact.mitgliedId;

		}

		this.loadContactAvatars(this.contacts.map(contact => contact.ebeId));

		});

	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	
	contactFilter( event: Event ) {

		if( !this.strContactFilter ) {
			this.contactsFiltered = this.contacts;
			return;
		}

		const filter = this.strContactFilter.toLocaleLowerCase();
		this.contactsFiltered = this.contacts.filter( c => {
			const name = (c.ebeVorname + " " + c.ebeName).toLocaleLowerCase();
			return( name.includes(filter) );
		});

	}

	clickToggleContactForGroup( id: string ) {
	
		if( this.groupChatEditorMode == GroupChatEditorMode.view ) return;

		const bRemoval = this.contactIdsNewGroup.includes(id);

		if( !this.chatGroup ) {

			if( !bRemoval ) this.bNotEnoughMembers = false;

			this.toggleContactForGroupLocal( id );

		}
		else {

			if( this.contactBeingEdited[ id ] ) return;

			this.bNotEnoughMembers = ( bRemoval && (this.contactIdsNewGroup.length <= 3) );
			if( this.bNotEnoughMembers ) return;

			this.contactBeingEdited[ id ] = true;

			const obs = bRemoval
				? this.chatService.removeUsersFromChatGroup( this.chatGroupId, [ id ] )
				: this.chatService.addUsersToChatGroup( this.chatGroupId, [ id ] );
			obs.subscribe( b => {

				this.contactBeingEdited[ id ] = false;
				if( b ) {
					this.toggleContactForGroupLocal( id );
					this.autoSaveDone();
				}

			});

		}
	
	}

	private toggleContactForGroupLocal( id: string ) {

		const n = this.contactIdsNewGroup.findIndex( contactId => contactId == id );
		if( n != -1 ) {
			this.contactIdsNewGroup.splice( n, 1 );
		}
		else {
			this.contactIdsNewGroup.push( id );
		}

	}


	onTitleChange() {

		this.bMissingTitle = false;

		if( this.chatGroup ) {
			this.groupEditSubject.next( this.newTitle );
		}

	}

	private autoSaveDone() {

		this.tmsLastSave = (new Date()).getTime();
		this.matSnackBar.open( "Erfolgreiche Speicherung", "Speichern" );

	}

	clickCreateGroup() {
	
		if( this.bInProgress ) return;
	
		this.bMissingTitle = !this.newTitle;
		if( this.bMissingTitle ) return;

		this.bNotEnoughMembers = ( this.contactIdsNewGroup.length < 3 );
		if( this.bNotEnoughMembers ) return;
	
		this.bInProgress = true;
		this.bSavingError = false;

		const chatGroup = new ChatGroup();
		chatGroup.title = this.newTitle;
		chatGroup.users = this.contactIdsNewGroup;

		this.chatService.createChatGroup( chatGroup ).subscribe( savedGroup => {
	
			if( !savedGroup ) {
				this.bInProgress = false;
				this.bSavingError = true;
				return;
			}

			this.chatGroup = savedGroup;

			const chat = new Chat();
			chat.title = savedGroup.title;
			chat.chatType = ChatType.userGroup;
			chat.sourceId = savedGroup.id;
			// the Backend doesn't accept the notification boolean here

			this.chatService.createChat( chat ).subscribe( chat => {

				if( !chat ) {
					this.bInProgress = false;
					this.bSavingError = true;
					return;
				}

				this.chatId = chat.id;

				if( this.bNotifications ) {

					this.chatService.setChatNotification( chat.id, true ).subscribe( b => {

						this.bInProgress = false;
						if( !b ) {
							this.bSavingError = true;
							this.bNotifications = !this.bNotifications;
						}
						else {
							this.router.navigate( [ "/Mitgliederaustausch" ], { queryParams: { chatId: chat.id } } );
						}
			
					} );
			
				}
				else {

					this.router.navigate( [ "/Mitgliederaustausch" ], { queryParams: { chatId: chat.id } } );

				}


			});

		});
	
	}

	onNotificationClick() {

		if( !this.chatId ) return;
		if( this.bInProgress ) return;

		this.bInProgress = true;
		this.bSavingError = false;
		this.chatService.setChatNotification( this.chatId, this.bNotifications ).subscribe( b => {

			this.bInProgress = false;
			if( !b ) {
				this.bSavingError = true;
				this.bNotifications = !this.bNotifications;
			}
			else {
				this.autoSaveDone();
			}

		} );

	}


	sortData( sort: Sort ) {

		if( !sort.active || sort.direction === '' ) {
			return;
		}

		this.sortList( sort.direction, sort.active );

	}

	private sortList( direction: string, property: string ) {

		this.contactsFiltered = [ ...this.contactsFiltered ].sort( (a, b) => {

			let result = 0;
			switch( property ) {
				case 'contactName':
					const nameA = this.contactNames[ a.ebeId ];
					const nameB = this.contactNames[ b.ebeId ];
					result = nameA.localeCompare( nameB );
					break;
				case 'contactMiglied':
					break;
				default:
					break;
			}

			const isAsc = ( direction === 'asc' ) ? 1 : -1;
			return result * isAsc;

		});

	}

	private loadContactAvatars(ebeIds: (string | undefined)[]): void {
		const missing = Array.from(new Set(
			ebeIds.filter((id): id is string => !!id && !(id in this.contactAvatarUrls))
		));
		if (!missing.length) return;

		const sub = this.userProfileImageService.listMini(missing).pipe(take(1)).subscribe(result => {
			const next = { ...this.contactAvatarUrls };
			for (const id of missing) {
				next[id] = result.get(id) ?? null;
			}
			this.contactAvatarUrls = next;
		});
		this.subscriptions.add(sub);
	}

}



